import type { Payee } from '@/api/payees';

/**
 * 收款人详情的展示与复制。
 *
 * 🔴 字段名不硬编码：表单结构由 Airwallex 按「国家 × 币种 × 汇款方式」下发
 * （PRODUCT.md §4.1），这里只能按 formValues 里实际存在的 path 来渲染。
 * 想给每个字段配一个中文名，就等于承诺跟进全球 100+ 个清算系统的字段变更。
 */

/** `beneficiary.bank_details.account_number` → `Account number` */
export function humanizePath(path: string): string {
  const last = path.split('.').pop() ?? path;
  const words = last.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export interface DetailField {
  path: string;
  label: string;
  value: string;
}

/**
 * 把 formValues 摊平成可展示的字段列表。
 * 空值直接丢掉 —— 一屏「—」没有任何信息量。
 */
export function detailFields(payee: Payee): DetailField[] {
  return Object.entries(payee.formValues ?? {})
    .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
    .map(([path, v]) => ({ path, label: humanizePath(path), value: String(v) }))
    .sort((a, b) => a.path.localeCompare(b.path));
}

/** 来源的一句话说明。列表和详情共用，保证两处说法一致。 */
export function originSummary(payee: Payee): string {
  const o = payee.origin;
  if (o.kind === 'external') {
    const link = o.linkTitle ? `「${o.linkTitle}」` : '收集链接';
    // 链接没开邮箱验证时拿不到填写人邮箱 —— 那就只说「对方」，不编一个出来
    const who = o.filledByEmail ? `${o.filledByEmail} 通过` : '对方通过';
    const sender = o.linkCreatedByName ? `，${o.linkCreatedByName} 发出` : '';
    return `${who}${link}填写${sender}`;
  }
  return o.createdByName ? `${o.createdByName} 在站内添加` : '站内添加';
}

/**
 * 一键复制的文本。给人发消息用，所以是纯文本、一行一个字段，
 * 短 id 放在第一行 —— 对方回过来时报的就是它。
 */
export function payeeToClipboard(payee: Payee): string {
  const head = [
    `收款人 ${payee.shortId}${payee.accountName ? ` · ${payee.accountName}` : ''}`,
    payee.nickname ? `别名：${payee.nickname}` : null,
    `类型：${payee.entityType === 'COMPANY' ? '企业' : '个人'}`,
    `收款地区 / 币种：${payee.bankCountryCode ?? '—'} / ${payee.accountCurrency ?? '—'}`,
    `汇款方式：${[payee.transferMethod, payee.localClearingSystem].filter(Boolean).join(' / ') || '—'}`,
    payee.contactEmail ? `联系邮箱：${payee.contactEmail}` : null,
    `来源：${originSummary(payee)}`,
  ].filter((x): x is string => Boolean(x));

  const fields = detailFields(payee).map((f) => `${f.label}: ${f.value}`);
  return [...head, '', ...fields].join('\n');
}
